import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { overtimeApprovalService } from '@/api/domains/overtime/overtimeApproval.service';
import { OVERTIME_BULK_OUTCOME } from '@/api/domains/overtime/overtimeApproval.types';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import type { ApiResponse } from '@/api/shared';
import type {
  OvertimeApprovalRow,
  OvertimeBulkOutcome,
  OvertimeConflict,
} from '@/api/domains/overtime/overtimeApproval.types';
import type { TableAction } from '@/components/ui/tables/Table.types';


export interface UseOvertimeApprovalRowActionsParams {
  monthlyCutId: number | null;
  isMonthlyCutClosed: boolean;
  refetch: () => void;
  /**
   * Perfil efectivo REVISOR (ASIS-5358): el menú de fila ofrece Justificar en lugar de Aprobar y
   * Reversar, que pertenecen a la otra mitad del flujo y que el backend le rechaza con 403.
   */
  isReviewerView?: boolean;
  /** Abre el panel lateral de justificación para una única fila. */
  onOpenJustifyPanel?: (row: OvertimeApprovalRow) => void;
}

export interface UseOvertimeApprovalRowActionsResult {
  actions: TableAction<OvertimeApprovalRow>[];
}

interface BulkOperationItem {
  employeeId: number;
  outcome: OvertimeBulkOutcome;
  conflict?: OvertimeConflict | null;
}

interface BulkOperationOut {
  items: BulkOperationItem[];
}

/** Textos de la operación, ya traducidos: uno por cada desenlace posible del colaborador. */
interface RowOperationMessages {
  success: string;
  error: string;
  notProcessed: string;
  conflictFallback: string;
}

/**
 * Acciones por fila del menú de tres puntos de Aprobación HHEE (ASIS-5371): Detalle (navegación),
 * Aprobar y Reversar. Aprobar reutiliza el mismo endpoint masivo de ASIS-5353 con un solo
 * `employeeId` — no existe endpoint unitario. Hook separado de `useOvertimeApproval` porque mezclar
 * tres acciones, dos confirmaciones y el manejo de conflicto ahí lo volvería inmanejable.
 */
export function useOvertimeApprovalRowActions(
  params: UseOvertimeApprovalRowActionsParams,
): UseOvertimeApprovalRowActionsResult {
  const {
    monthlyCutId, isMonthlyCutClosed, refetch,
    isReviewerView = false, onOpenJustifyPanel,
  } = params;

  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showWarning, openModal } = useUI();
  const { can } = usePagePermissions();
  const navigate = useNavigate();

  const canEdit = can('edit');

  // Guarda de reentrada: una fila no puede disparar dos operaciones a la vez (doble click en el
  // menú mientras la primera todavía está en vuelo).
  const inFlightRowIdsRef = useRef<Set<string | number>>(new Set());

  const buildConflictMessage = useCallback((conflict: OvertimeConflict | null | undefined) => {
    if (!conflict || !conflict.action) return null;

    const actionLabel = translate(
      conflict.action === 'approved'
        ? 'attendance:overtimeApproval.rowActions.conflictActionApproved'
        : 'attendance:overtimeApproval.rowActions.conflictActionReversed',
    );

    return translate('attendance:overtimeApproval.rowActions.conflict', {
      action: actionLabel,
      userName: conflict.userName,
    });
  }, [translate]);

  // ASIS-5355: apunta a la pantalla real de detalle de solo lectura, con `employeeIds` en CSV
  // (aunque sea un solo colaborador) para compartir el mismo query string que la acción masiva
  // "Detalle" de useOvertimeApprovalBulkActions.
  const navigateToDetail = useCallback((row: OvertimeApprovalRow) => {
    if (monthlyCutId === null) return;

    void navigate(
      `${ROUTE_DEFINITIONS.APROBAR_HHEE_DETALLE.path}?employeeIds=${row.id}&monthlyCutId=${monthlyCutId}`,
    );
  }, [monthlyCutId, navigate]);

  const runRowOperation = useCallback(async <T extends BulkOperationOut>(
    row: OvertimeApprovalRow,
    call: () => Promise<ApiResponse<T>>,
    successOutcome: OvertimeBulkOutcome,
    messages: RowOperationMessages,
  ): Promise<void> => {
    if (monthlyCutId === null || inFlightRowIdsRef.current.has(row.id)) return;

    inFlightRowIdsRef.current.add(row.id);
    try {
      const response = await call();

      if (!response.success || !response.data) {
        showApiError(response, messages.error);
        return;
      }

      const item = response.data.items.find((candidate) => candidate.employeeId === row.id);

      // Se ramifica sobre `outcome` y no sobre la presencia de `conflict`: el backend puede
      // devolver `alreadyResolved` sin detalle de auditoría localizable (dato legado incompleto),
      // y `notProcessed` no lleva conflicto alguno pese a que tampoco escribió nada.
      if (item?.outcome === successOutcome) {
        showSuccess(messages.success);
      } else if (item?.outcome === OVERTIME_BULK_OUTCOME.ALREADY_RESOLVED) {
        showWarning(buildConflictMessage(item.conflict) ?? messages.conflictFallback);
      } else {
        showWarning(messages.notProcessed);
      }

      // El refresco trae el estado real desde el backend y el menú recalcula sus opciones
      // automáticamente a partir de él — no hay que actualizar la fila a mano. Se hace incluso
      // cuando no se procesó nada: el estado pudo cambiar por acción de otro usuario.
      refetch();
    } finally {
      inFlightRowIdsRef.current.delete(row.id);
    }
  }, [monthlyCutId, showApiError, showWarning, showSuccess, refetch, buildConflictMessage]);

  const approveRow = useCallback((row: OvertimeApprovalRow) => {
    if (!canEdit || monthlyCutId === null) return;

    openModal('ConfirmModal', {
      variant: 'success',
      icon: 'checkCircle',
      title: translate('attendance:overtimeApproval.rowActions.approveConfirmTitle'),
      bodyText: translate('attendance:overtimeApproval.rowActions.approveConfirmBody', {
        employeeName: row.employeeName,
      }),
      confirmText: translate('attendance:overtimeApproval.rowActions.approve'),
      onConfirm: async () => {
        await runRowOperation(
          row,
          () => overtimeApprovalService.approveBulk({ monthlyCutId, employeeIds: String(row.id) }),
          OVERTIME_BULK_OUTCOME.APPROVED,
          {
            success: translate('attendance:overtimeApproval.rowActions.approveSuccess'),
            error: translate('attendance:overtimeApproval.rowActions.approveError'),
            notProcessed: translate('attendance:overtimeApproval.rowActions.approveNotProcessed', {
              employeeName: row.employeeName,
            }),
            conflictFallback: translate('attendance:overtimeApproval.rowActions.conflictGeneric'),
          },
        );
      },
    });
  }, [canEdit, monthlyCutId, openModal, translate, runRowOperation]);

  const reverseRow = useCallback((row: OvertimeApprovalRow) => {
    if (!canEdit || monthlyCutId === null) return;

    openModal('ConfirmModal', {
      variant: 'error',
      icon: 'alert',
      title: translate('attendance:overtimeApproval.rowActions.reverseConfirmTitle'),
      bodyText: translate('attendance:overtimeApproval.rowActions.reverseConfirmBody', {
        employeeName: row.employeeName,
      }),
      confirmText: translate('attendance:overtimeApproval.rowActions.reverse'),
      onConfirm: async () => {
        await runRowOperation(
          row,
          () => overtimeApprovalService.reverse({ monthlyCutId, employeeIds: String(row.id) }),
          OVERTIME_BULK_OUTCOME.REVERSED,
          {
            success: translate('attendance:overtimeApproval.rowActions.reverseSuccess'),
            error: translate('attendance:overtimeApproval.rowActions.reverseError'),
            notProcessed: translate('attendance:overtimeApproval.rowActions.reverseNotProcessed', {
              employeeName: row.employeeName,
            }),
            conflictFallback: translate('attendance:overtimeApproval.rowActions.conflictGeneric'),
          },
        );
      },
    });
  }, [canEdit, monthlyCutId, openModal, translate, runRowOperation]);

  /**
   * Justificar unitario (ASIS-5358): solo abre el panel lateral. La ejecución vive en
   * `useOvertimeApprovalBulkActions.justifySelection`, con la fila como lote de uno, para no
   * duplicar la llamada al servicio ni el desglose de mensajes por desenlace. El lote NO pasa por
   * la selección de la tabla: los checkboxes marcados quedan como estaban.
   */
  const justifyRow = useCallback((row: OvertimeApprovalRow) => {
    if (!canEdit || isMonthlyCutClosed) return;

    onOpenJustifyPanel?.(row);
  }, [canEdit, isMonthlyCutClosed, onOpenJustifyPanel]);

  // Visibilidad por estado: se filtra, no se deshabilita (el criterio dice "no muestra la opción").
  // Detalle es la única acción que siempre se ofrece, incluso sin permiso de edición o con el corte
  // cerrado — es solo navegación.
  const actions = useMemo<TableAction<OvertimeApprovalRow>[]>(() => [
    {
      id: 'detail',
      label: translate('common:tableActions.detail'),
      icon: 'eye',
      onClick: navigateToDetail,
    },
    // Justificar (ASIS-5358): la acción unitaria del revisor. Reutiliza el mismo modal y el mismo
    // endpoint que la masiva, con un solo colaborador. No se oculta por estado: re-justificar una
    // fila ya Justificada es la única vía que tiene el revisor para corregir el tipo, porque el
    // backend reemplaza su marca previa y la reversa del revisor no entra en este ticket.
    {
      id: 'justify',
      label: translate('attendance:overtimeApproval.bulkJustify.action'),
      icon: 'checkCircle',
      onClick: justifyRow,
      hidden: () => !isReviewerView || !canEdit || isMonthlyCutClosed,
    },
    {
      id: 'approve',
      label: translate('common:tableActions.approve'),
      icon: 'checkCircle',
      onClick: approveRow,
      hidden: (row) => isReviewerView || !canEdit || isMonthlyCutClosed || row.status === 'Aprobada',
    },
    {
      id: 'reverse',
      label: translate('attendance:overtimeApproval.rowActions.reverse'),
      icon: 'sync',
      variant: 'danger',
      onClick: reverseRow,
      hidden: (row) => isReviewerView || !canEdit || isMonthlyCutClosed || row.status === 'Pendiente',
    },
  ], [
    translate, canEdit, isMonthlyCutClosed, navigateToDetail, approveRow, reverseRow,
    isReviewerView, justifyRow,
  ]);

  return { actions };
}

export default useOvertimeApprovalRowActions;
